import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Upload, AlertTriangle, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const VALID_STATUSES = ["present", "absent", "late", "excused"] as const;
type AttStatus = typeof VALID_STATUSES[number];

interface ParsedRow {
  sn: number;
  studentId: string;
  formIvIndex: string;
  studentName: string;
  status: string;
  matchedStudentId: string | null;
  errors: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  session: {
    id: string;
    class: string | null;
    module_code: string | null;
    topic: string | null;
    session_date: string;
  };
  onImported?: () => void;
}

export function SessionBulkAttendanceDialog({ open, onOpenChange, session, onImported }: Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<ParsedRow[] | null>(null);

  function reset() {
    setRows(null);
    setParsing(false);
    setSaving(false);
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets["Attendance"] ?? wb.Sheets[wb.SheetNames[0]];
      const json: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });

      // Load students for class to match
      const { data: students } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, form_iv_index")
        .eq("class", session.class ?? "")
        .eq("status", "active");

      const byStudentId = new Map((students ?? []).map((s) => [String(s.student_number ?? "").trim().toLowerCase(), s]));
      const byFormIv = new Map((students ?? []).map((s) => [String(s.form_iv_index ?? "").trim().toLowerCase(), s]));

      const parsed: ParsedRow[] = json.map((r, i) => {
        const sid = String(r["Student ID"] ?? "").trim();
        const fiv = String(r["Form IV Index"] ?? "").trim();
        const name = String(r["Student Name"] ?? "").trim();
        const status = String(r["Status"] ?? "").trim().toLowerCase();
        const errors: string[] = [];
        let match = sid ? byStudentId.get(sid.toLowerCase()) : undefined;
        if (!match && fiv) match = byFormIv.get(fiv.toLowerCase());
        if (!match) errors.push("Student not found");
        if (!status) errors.push("Status empty (skipped)");
        else if (!VALID_STATUSES.includes(status as AttStatus)) errors.push(`Invalid status "${status}"`);
        return {
          sn: Number(r["SN"]) || i + 1,
          studentId: sid,
          formIvIndex: fiv,
          studentName: name,
          status,
          matchedStudentId: match?.id ?? null,
          errors,
        };
      });

      setRows(parsed);
    } catch (err: any) {
      toast({ title: "Parse error", description: err.message, variant: "destructive" });
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  }

  async function handleConfirm() {
    if (!rows) return;
    const valid = rows.filter((r) => r.errors.length === 0 && r.matchedStudentId);
    if (valid.length === 0) {
      toast({ title: "No valid rows to import", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      // Fetch existing records for this session to decide insert vs update
      const studentIds = valid.map((r) => r.matchedStudentId!);
      const { data: existing } = await supabase
        .from("attendance_records")
        .select("id, student_id")
        .eq("session_id", session.id)
        .in("student_id", studentIds);

      const existingMap = new Map((existing ?? []).map((e) => [e.student_id, e.id]));

      const toInsert: any[] = [];
      const updates: { id: string; status: string }[] = [];
      for (const r of valid) {
        const existingId = existingMap.get(r.matchedStudentId!);
        if (existingId) {
          updates.push({ id: existingId, status: r.status });
        } else {
          toInsert.push({
            student_id: r.matchedStudentId,
            session_id: session.id,
            attendance_type: "session",
            attendance_date: session.session_date,
            status: r.status,
            scanned_by: user?.id,
          });
        }
      }

      if (toInsert.length) {
        const { error } = await supabase.from("attendance_records").insert(toInsert);
        if (error) throw error;
      }
      for (const u of updates) {
        const { error } = await supabase.from("attendance_records").update({ status: u.status }).eq("id", u.id);
        if (error) throw error;
      }

      toast({
        title: "Import complete",
        description: `${toInsert.length} added, ${updates.length} updated, ${rows.length - valid.length} skipped`,
      });
      onImported?.();
      reset();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Save error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const validCount = rows?.filter((r) => r.errors.length === 0).length ?? 0;
  const errorCount = (rows?.length ?? 0) - validCount;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Bulk Upload Attendance</DialogTitle>
          <DialogDescription>
            {session.module_code} — {session.topic} ({session.class})
          </DialogDescription>
        </DialogHeader>

        {!rows && (
          <div className="space-y-3">
            <Label>Select filled .xlsx template</Label>
            <Input type="file" accept=".xlsx,.xls" onChange={handleFile} disabled={parsing} />
            <p className="text-xs text-muted-foreground">
              Status column accepts: present, absent, late, excused. Empty rows are skipped.
            </p>
            {parsing && <div className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Parsing...</div>}
          </div>
        )}

        {rows && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" /> {validCount} valid
              </Badge>
              {errorCount > 0 && (
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                  <AlertTriangle className="h-3 w-3 mr-1" /> {errorCount} skipped
                </Badge>
              )}
            </div>
            <ScrollArea className="max-h-[360px] border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">SN</TableHead>
                    <TableHead>Student ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 100).map((r) => (
                    <TableRow key={r.sn} className={r.errors.length ? "bg-red-50/50" : ""}>
                      <TableCell className="text-xs">{r.sn}</TableCell>
                      <TableCell className="text-xs font-mono">{r.studentId || r.formIvIndex || "—"}</TableCell>
                      <TableCell className="text-xs">{r.studentName || "—"}</TableCell>
                      <TableCell className="text-xs">{r.status || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {r.errors.length ? (
                          <span className="text-red-600">{r.errors.join("; ")}</span>
                        ) : (
                          <span className="text-green-700">OK</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length > 100 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  Showing first 100 of {rows.length} rows
                </p>
              )}
            </ScrollArea>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={reset} disabled={saving}>Reset</Button>
              <Button onClick={handleConfirm} disabled={saving || validCount === 0}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Import {validCount} rows
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
